import { useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export async function downloadFile(url, params, fallback) {
  const res = await api.get(url, { params, responseType: "blob" });
  const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || fallback;
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60000);
}

const download = (runId, format) => downloadFile(`/runs/${runId}/export`, { format }, `warrant-run.${format}`);

export async function blobError(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    const text = await data.text();
    try {
      e.response.data = JSON.parse(text);
    } catch {
      e.response.data = { detail: text || "Download failed. Please try again." };
    }
  }
  return errorMessage(e);
}

export const ExportRunMenu = ({ runId }) => {
  const [busy, setBusy] = useState(false);
  const run = async (format) => {
    setBusy(true);
    try {
      await download(runId, format);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(await blobError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy} data-testid="run-detail-export-button">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => run("pdf")} data-testid="run-export-pdf"><FileText className="mr-2 h-4 w-4" /> Download PDF</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => run("csv")} data-testid="run-export-csv"><FileSpreadsheet className="mr-2 h-4 w-4" /> Download CSV</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
